import { globalRef, type Maybe } from '@amazon/vinyl-util'
import { type MetricsEntityFlavor, podcastFlavor } from '@/metrics/entityFlavor'
import { metricAppVersion, metricDeviceTimeZone } from '@/metrics/metricContext'
import {
    getMetrixUploader,
    type MetrixAction,
    toAdditionalDetails,
    type WireDetails,
} from '@/metrics/metrixUploader'
import { type UiEventContext, uiEventContext, uiMetricsStateRef } from '@/metrics/uiEventContext'

export type { UiEventContext } from '@/metrics/uiEventContext'

// GH's MusicMetrix UI family (`uiPageView`, `uiClick`, `uiContentView`): the page a
// customer is on, and what they did there. The router reports each routed page
// (`routePageViews.ts`); features report their clicks and content views on it.

/** GH's `EntityType` values amp reports. */
export type MetricsEntityType =
    | 'ALBUM'
    | 'ARTIST'
    | 'AUDIOBOOK'
    | 'AUDIOBOOKS_CATEGORY'
    | 'CONCERT'
    | 'GENRE'
    | 'LIVE_EVENT'
    | 'MERCH'
    | 'PLAYLIST'
    | 'PODCAST_CATEGORY'
    | 'PODCAST_EPISODE'
    | 'PODCAST_SHOW'
    | 'PROFILE'
    | 'SEARCH_QUERY'
    | 'STATION'
    | 'TRACK'
    | 'VIDEO'
    | 'VISUAL_AD'

/** GH's `EntityIDType` for each entity type (`convertFireflyEntityInfo`). */
const ENTITY_ID_TYPES = {
    ALBUM: 'ASIN',
    ARTIST: 'ASIN',
    AUDIOBOOK: 'AUDIOBOOK_ID',
    AUDIOBOOKS_CATEGORY: 'CATEGORY_ID',
    CONCERT: 'CONCERT_ID',
    GENRE: 'GENRE_NAME',
    LIVE_EVENT: 'LIVE_EVENT_ID',
    MERCH: 'ASIN',
    PLAYLIST: 'PLAYLIST_ID',
    PODCAST_CATEGORY: 'PODCAST_CATEGORY_ID',
    PODCAST_EPISODE: 'PODCAST_EPISODE_ID',
    PODCAST_SHOW: 'PODCAST_SHOW_ID',
    PROFILE: 'PROFILE_ID',
    SEARCH_QUERY: 'SEARCH_QUERY_ID',
    STATION: 'STATION_SEED_ID',
    TRACK: 'ASIN',
    VIDEO: 'ASIN',
    VISUAL_AD: 'AD_ID',
} as const satisfies Record<MetricsEntityType, string>

export type MetricsEntityIdType = (typeof ENTITY_ID_TYPES)[MetricsEntityType]

/** An entity as GH's UI events name it; GH sends a bare type when it has no id. */
export type MetricsEntity =
    | {
          readonly type: MetricsEntityType
          readonly idType: MetricsEntityIdType
          readonly id: string
          readonly flavor?: MetricsEntityFlavor
      }
    | { readonly type: MetricsEntityType }

export function metricsEntity(
    type: MetricsEntityType,
    id: string,
    flavor?: Maybe<MetricsEntityFlavor>
): MetricsEntity {
    return { type, idType: ENTITY_ID_TYPES[type], id, ...(flavor ? { flavor } : null) }
}

const TYPENAME_ENTITY_TYPES: Readonly<Record<string, MetricsEntityType>> = {
    Album: 'ALBUM',
    Artist: 'ARTIST',
    Audiobook: 'AUDIOBOOK',
    LiveEvent: 'LIVE_EVENT',
    Merch: 'MERCH',
    Playlist: 'PLAYLIST',
    PodcastEpisode: 'PODCAST_EPISODE',
    PodcastShow: 'PODCAST_SHOW',
    Station: 'STATION',
    Track: 'TRACK',
    Video: 'VIDEO',
}

interface FireflyMetricsNode {
    readonly __typename?: Maybe<string>
    readonly id?: Maybe<string>
    readonly mediaType?: Maybe<string>
    readonly playbackInformation?: Maybe<{
        readonly videoUrl?: Maybe<string>
        readonly [field: string]: unknown
    }>
}

// Without `playbackInformation` selected a video episode can't be told apart, so it goes unflavored.
function podcastEpisodeFlavor(node: FireflyMetricsNode): Maybe<MetricsEntityFlavor> {
    if (!('playbackInformation' in node)) return null
    return podcastFlavor(!!node.playbackInformation?.videoUrl)
}

/** GH `convertFireflyEntityInfo`: a FireFly entity as UI events name it; a music video is a VIDEO. */
export function fireflyMetricsEntity(node: FireflyMetricsNode): Maybe<MetricsEntity> {
    const type = node.__typename ? TYPENAME_ENTITY_TYPES[node.__typename] : undefined
    if (!type || !node.id) return null
    const flavor = type === 'PODCAST_EPISODE' ? podcastEpisodeFlavor(node) : null
    return metricsEntity(node.mediaType === 'VIDEO' ? 'VIDEO' : type, node.id, flavor)
}

/** GH's `PageType` values amp reports. */
export type MetricsPageType =
    | 'NOT_FOUND'
    | 'activityFeed'
    | 'albumDetail'
    | 'artistDetail'
    | 'audiobookDetails'
    | 'audiobooksCategory'
    | 'audiobooksHome'
    | 'browseFind'
    | 'browseGema'
    | 'browseHome'
    | 'browseHomeMusic'
    | 'browseNew'
    | 'browsePlaylists'
    | 'browseSearchHistory'
    | 'browseSearchSuggestions'
    | 'browseStations'
    | 'browseVideo'
    | 'canaryWhoIsListening'
    | 'charts'
    | 'cloudLibrary-albums'
    | 'cloudLibrary-artists'
    | 'cloudLibrary-followedPlaylists'
    | 'cloudLibrary-playlists'
    | 'cloudLibrary-songs'
    | 'cloudLibraryAudiobooks'
    | 'crossContentGema'
    | 'detail-LiveEvent'
    | 'detail-Merch'
    | 'detailConcert'
    | 'detail_fanPoll'
    | 'eventsHub'
    | 'libraryLandingPage'
    | 'maestroLandingPage'
    | 'maestroResults'
    | 'myLibraryFrontPageRecentlyPlayed'
    | 'newReleases'
    | 'playQueue'
    | 'playlists'
    | 'playlistCollaborationInvitation'
    | 'playlistDetail'
    | 'podcastEpisodeDetail'
    | 'podcastShowDetail'
    | 'podcastsCategoryShows'
    | 'podcastsCuratedPage'
    | 'podcastsHomePage'
    | 'podcastsLibraryLatest'
    | 'podcastsLibraryPlaylist'
    | 'podcastsLibraryShows'
    | 'podcastsPopularShows'
    | 'profile'
    | 'profileFollower'
    | 'profileFollowing'
    | 'profilePlaylists'
    | 'searchResults'
    | 'seeAll-NewAlbums'
    | 'seeAll-NewPLAYLISTS'
    | 'seeAll-NewTracks'
    | 'seeAll-artistAlbums'
    | 'seeAll-artistTracks'
    | 'seeAll-popularAlbums'
    | 'seeAll-popularPlaylists'
    | 'seeAll-popularStations'
    | 'seeAll-popularTracks'
    | 'seeAll_ArtistPodcasts'
    | 'seeAll_ArtistVideos'
    | 'seeAll_Recommendation'
    | 'seeAll_SearchResults'
    | 'settings'
    | 'shop-Merch'
    | 'stations'
    | 'trackDetail'

/** The page a UI event happened on. */
export interface MetricsPage {
    readonly pageType: MetricsPageType
    readonly pageSubType?: string
    readonly entity?: MetricsEntity
}

/** GH's `InteractionType`; a pointer click is `CLICK`. */
export type MetricsInteractionType = 'CLICK' | 'HOVER' | 'KEYBOARD' | 'TAP' | 'TOGGLE'

/** What a click acted on and where, beyond the page it happened on. */
export interface UiClick {
    /** GH's `ActionType` (`play`, `selectAlbum`, `goSearch`…). */
    readonly actionType: string
    readonly interactionType?: MetricsInteractionType
    readonly entity?: Maybe<MetricsEntity>
    /** The page the click happened on, when it is not the current one. */
    readonly page?: MetricsPage
    readonly actionDetail?: string
    readonly blockRef?: string
    readonly contentInfoInstId?: string
    /** Who authored the content: `client` for the app's own surfaces. */
    readonly contentSrc?: string
    readonly contentName?: string
    readonly containerIndex?: number
    readonly entityPos?: number
    readonly refMarker?: string
    readonly searchQuery?: string
    readonly startAsStation?: boolean
    /** Where the click leads; a relative path is resolved against the app. */
    readonly targetUrl?: string
    /** The URL the click acted on (a share's link), when it is not the page's. */
    readonly url?: string
}

/** What a control's clicks report beyond their action: the entity and where it sits. */
export type ClickContext = Omit<UiClick, 'actionType'>

/** A detail page's header control acting on `entity`, as GH's content context names it. */
export function detailHeaderClicks(entity: MetricsEntity): ClickContext {
    return { entity, contentName: 'header' }
}

/** An entity that came into view, and its place in its container. */
export interface MetricsViewedEntity {
    readonly entity: MetricsEntity
    readonly entityPos?: number
}

/** Content that came into view, as GH's `ContentInfo` names it. */
export interface MetricsContentInfo {
    readonly contentName?: string
    readonly contentSrc?: string
    readonly containerType?: string
    readonly containerIndex?: number
    readonly numItemsAccessible?: number
    readonly entities?: readonly MetricsViewedEntity[]
}

/** A surface of the page that came into view. */
export interface UiContentView {
    readonly page?: MetricsPage
    readonly contentSrc?: string
    readonly contentName?: string
    readonly entities?: readonly MetricsViewedEntity[]
    /** What came into view; by default the named surface and its entities. */
    readonly contentInfo?: readonly MetricsContentInfo[]
}

interface ViewedContentState {
    visit: number
    readonly viewed: Map<MetricsContentInfo, MetricsViewedEntity[]>
    flush: Maybe<ReturnType<typeof setTimeout>>
}

const viewedContentRef = globalRef<ViewedContentState>(() => ({
    visit: 0,
    viewed: new Map(),
    flush: null,
}))

const VIEWED_CONTENT_DEBOUNCE_SECONDS = 1

/** What a routed page reports once it has loaded (a live event's state). */
export interface RoutedPageDetails {
    readonly pageSubType: string
}

/**
 * A routed page came on stage. One that `awaitsDetails` reports its view once the
 * page describes itself ({@link describeRoutedPage}), as GH's waits for its data.
 */
export function showRoutedPage(view: Element, page: MetricsPage, awaitsDetails: boolean): void {
    leavePage()
    const state = uiMetricsStateRef.value
    state.routed = { view, page, awaitsDetails }
    state.reportedView = null
    if (!awaitsDetails) {
        reportRoutedPageView(view, page)
        return
    }
    const details = state.described.get(view)
    if (details) reportRoutedPageView(view, { ...page, ...details })
}

/** A routed page's loaded details; a changed subtype on the shown page is a new view. */
export function describeRoutedPage(view: Element, details: RoutedPageDetails): void {
    const state = uiMetricsStateRef.value
    const previous = state.described.get(view)
    state.described.set(view, details)
    const routed = state.routed
    if (routed?.view !== view || !routed.awaitsDetails) return
    if (previous?.pageSubType === details.pageSubType) return
    reportRoutedPageView(view, { ...routed.page, ...details })
}

/** GH's `metricsStore.pageType`: the page the customer is on, which playback stamps. */
export function currentPageType(): Maybe<MetricsPageType> {
    return uiMetricsStateRef.value.current?.pageType
}

/**
 * A view of `page` within a page (Find's search history, then its suggestions). One `within` a
 * routed view follows that page's own view, as GH's in-page views follow their page's.
 */
export function reportPageView(page: MetricsPage, within?: Element): void {
    const state = uiMetricsStateRef.value
    if (within && state.reportedView !== within) {
        const pending = state.pendingViews.get(within) ?? []
        state.pendingViews.set(within, [...pending, page])
        return
    }
    leavePage()
    state.current = page
    getMetrixUploader().enqueue(uiPageViewAction(page, uiEventContext()))
}

/** Views `page` over the routed page until the returned callback hands the page back. */
export function reportOverlayPageView(page: MetricsPage): () => void {
    const state = uiMetricsStateRef.value
    const underneath = state.current
    reportPageView(page)
    return () => {
        if (state.current === page) state.current = underneath
    }
}

/** `context` is the one captured as the click happened, when it is reported later. */
export function reportClick(click: UiClick, context: UiEventContext = uiEventContext()): void {
    getMetrixUploader().enqueue(uiClickAction(click, context))
}

export function reportContentView(
    view: UiContentView,
    context: UiEventContext = uiEventContext()
): void {
    getMetrixUploader().enqueue(uiContentViewAction(view, context))
}

/**
 * The routed page opened again from a link to it, at `openedAt`: GH pushes a fresh copy of the
 * page, which reports its view.
 */
export function reportRoutedPageAgain(openedAt: number): void {
    const { routed, reportedView, described } = uiMetricsStateRef.value
    if (!routed || reportedView !== routed.view) return
    const page = { ...routed.page, ...described.get(routed.view) }
    leavePage()
    uiMetricsStateRef.value.current = page
    getMetrixUploader().enqueue(
        uiPageViewAction(page, {
            ...uiEventContext(),
            loadTimeMilliseconds: Math.max(1, Date.now() - openedAt),
        })
    )
}

/** The routed page under the app's panels and overlays. */
export function routedPage(): Maybe<MetricsPage> {
    const { routed, described } = uiMetricsStateRef.value
    if (!routed) return null
    const details = described.get(routed.view)
    return details ? { ...routed.page, ...details } : routed.page
}

/** Counts the pages the customer has been on; what they saw belongs to the visit they saw it on. */
export function pageVisit(): number {
    return viewedContentRef.value.visit
}

/**
 * An entity of `content` the customer has looked at. GH reports what one look took in
 * as a single content view a second after the last entity came into view, and amp
 * also reports it when the customer leaves the page.
 */
export function reportViewedEntity(content: MetricsContentInfo, viewed: MetricsViewedEntity): void {
    const state = viewedContentRef.value
    const entities = state.viewed.get(content)
    if (entities) entities.push(viewed)
    else state.viewed.set(content, [viewed])
    if (state.flush !== null) clearTimeout(state.flush)
    state.flush = setTimeout(flushViewedContent, VIEWED_CONTENT_DEBOUNCE_SECONDS * 1000)
}

function flushViewedContent(): void {
    const state = viewedContentRef.value
    if (state.flush !== null) clearTimeout(state.flush)
    state.flush = null
    if (state.viewed.size === 0) return
    const contentInfo = [...state.viewed].map(([content, entities]) => ({ ...content, entities }))
    state.viewed.clear()
    reportContentView({ contentInfo })
}

function leavePage(): void {
    flushViewedContent()
    viewedContentRef.value.visit++
}

function reportRoutedPageView(view: Element, page: MetricsPage): void {
    const state = uiMetricsStateRef.value
    state.current = page
    state.reportedView = view
    getMetrixUploader().enqueue(
        uiPageViewAction(page, { ...uiEventContext(), loadTimeMilliseconds: pageLoadTime() })
    )
    const pending = state.pendingViews.get(view)
    state.pendingViews.delete(view)
    for (const inPage of pending ?? []) reportPageView(inPage, view)
}

// GH measures from the navigation to the loaded page, floored at 1 ms.
function pageLoadTime(): Maybe<number> {
    const startedAt = window.pageStartedAt
    return startedAt === undefined ? null : Math.max(1, Date.now() - startedAt)
}

function pageDetails(page: Maybe<MetricsPage>): WireDetails {
    const entity = page?.entity
    return {
        pageType: page?.pageType ?? 'unknown',
        pageSubType: page?.pageSubType,
        ...(entity ? entityDetails('pageEntity', entity) : null),
    }
}

function entityDetails(prefix: 'entity' | 'pageEntity', entity: MetricsEntity): WireDetails {
    return {
        [`${prefix}Type`]: entity.type,
        ...('id' in entity
            ? { [`${prefix}IdType`]: entity.idType, [`${prefix}Id`]: entity.id }
            : null),
    }
}

function uiAction(
    actionName: string,
    context: UiEventContext,
    details: WireDetails,
    contentInfo?: readonly MetricsContentInfo[]
): MetrixAction {
    return {
        actionName,
        timestamp: context.timestamp / 1000,
        additionalDetails: toAdditionalDetails({
            ...details,
            appVersion: metricAppVersion,
            deviceTimeZone: metricDeviceTimeZone,
        }),
        ...(contentInfo?.length ? { contentInfo: contentInfo.map(contentInfoRow) } : null),
    }
}

// The MIS model declares `contentInfo` a list of string maps, so the SDK sends `entities` as JSON.
function contentInfoRow({ entities, ...content }: MetricsContentInfo): Record<string, string> {
    return toAdditionalDetails({
        ...content,
        entities: entities?.length ? JSON.stringify(entities.map(viewedEntityDetails)) : null,
    })
}

function viewedEntityDetails({ entity, entityPos }: MetricsViewedEntity): Record<string, string> {
    return toAdditionalDetails({ ...entityDetails('entity', entity), entityPos })
}

/** GH `sendUiPageViewMetric`: the page, the URL it loaded at and the window's orientation. */
export function uiPageViewAction(page: MetricsPage, context: UiEventContext): MetrixAction {
    return uiAction('uiPageView', context, {
        ...pageDetails(page),
        loadTimeMilliseconds: context.loadTimeMilliseconds,
        url: context.url,
        viewType: context.viewType,
        refMarker: context.refMarker,
    })
}

/** GH `sendUiClickMetric`: a click names its page (the current one by default). */
export function uiClickAction(click: UiClick, context: UiEventContext): MetrixAction {
    return uiAction('uiClick', context, {
        ...pageDetails(click.page ?? context.current),
        url: click.url ?? context.url,
        actionType: click.actionType,
        interactionType: click.interactionType ?? 'CLICK',
        ...(click.entity ? entityDetails('entity', click.entity) : null),
        entityFlavor: click.entity && 'flavor' in click.entity ? click.entity.flavor : null,
        actionDetail: click.actionDetail,
        blockRef: click.blockRef,
        contentInfoInstId: click.contentInfoInstId,
        contentSrc: click.contentSrc ?? (click.contentName === undefined ? null : 'client'),
        contentName: click.contentName,
        containerIndex: click.containerIndex,
        entityPos: click.entityPos,
        refMarker: click.refMarker,
        searchQuery: click.searchQuery,
        startAsStation: click.startAsStation,
        targetUrl:
            click.targetUrl === undefined ? null : new URL(click.targetUrl, context.url).href,
    })
}

/** GH `sendUiContentViewMetric`: what came into view, on its page. */
export function uiContentViewAction(view: UiContentView, context: UiEventContext): MetrixAction {
    return uiAction(
        'uiContentView',
        context,
        {
            ...pageDetails(view.page ?? context.current),
            url: context.url,
            contentSrc: view.contentSrc ?? (view.contentName === undefined ? null : 'client'),
            contentName: view.contentName,
        },
        view.contentInfo ?? namedContent(view)
    )
}

function namedContent({ contentName, contentSrc, entities }: UiContentView): MetricsContentInfo[] {
    if (contentName === undefined) return []
    return [
        {
            contentName,
            ...(contentSrc === undefined ? null : { contentSrc }),
            ...(entities ? { entities } : null),
        },
    ]
}
